// This strategy is used to keep the iOS Webview alive and let js code run in background
// by calling setOrientationProperties every second.
// This is a workaround for a known issue where iOS Webview pauses javascript execution after a few seconds without activity.
// See https://jira.ogury.io/browse/SL3-4697 for more details.
import { isFeatureEnable } from '../../../../common/js/features-enabling';
import {
  AfterVideoLoadedEvent,
  FormatContext,
  ILoadingStrategy
} from './loading-strategy';
import { RootStore } from '../../../../common/js/stores/root.store';
import { reaction } from '../../../../common/js/utils/preact-utils';

const KEEP_ALIVE_INTERVAL = 1000; // in ms

export class iOSWebviewKeepAliveStrategy implements ILoadingStrategy {
  setIntervalHandle: ReturnType<typeof setInterval> | null = null;

  isRunnable(_formatContext: FormatContext): boolean {
    const isRunnable = isFeatureEnable('sdkiOSKeepAlive');
    if (isRunnable) {
      this.onBeforeRender(_formatContext);
    }
    return isRunnable;
  }

  alterConfiguration(formatContext: FormatContext): FormatContext {
    return formatContext;
  }

  afterVideoLoaded(event: AfterVideoLoadedEvent): AfterVideoLoadedEvent {
    const rootStore = RootStore.getInstance();
    const videoStore = rootStore.videoStore;
    const destroyVideoStartedReaction = reaction(
      () => videoStore.hasVideoStarted.value,
      (started: boolean) => {
        if (started && this.setIntervalHandle) {
          clearInterval(this.setIntervalHandle);
          this.setIntervalHandle = null;
          destroyVideoStartedReaction && destroyVideoStartedReaction();
        }
      }
    );

    return event;
  }

  onBeforeRender(formatContext: FormatContext): void {
    let orientation = 'auto';
    if (formatContext.config && formatContext.config.orientation) {
      orientation = formatContext.config.orientation;
    }

    this.setIntervalHandle = setInterval(() => {
      if (orientation !== 'auto') {
        RootStore.getInstance().adContainerStore &&
          RootStore.getInstance().adContainerStore.adContainerPort.setOrientationProperties(
            {
              allowOrientationChange: false,
              forceOrientation: orientation
            }
          );
      } else {
        RootStore.getInstance().adContainerStore &&
          RootStore.getInstance().adContainerStore.adContainerPort.setOrientationProperties(
            {
              allowOrientationChange: true,
              forceOrientation: 'none'
            }
          );
      }
    }, KEEP_ALIVE_INTERVAL);
  }
}
